import { WebContainer, FileSystemTree } from "@webcontainer/api";

// services/webcontainer.ts
let webcontainerPromise: Promise<any> | null = null;

type ServerReadyCb = (port: number, url: string) => void;
const serverReadyCbs = new Set<ServerReadyCb>();
let serverReadyWired = false;

export async function getWebContainer() {
  if (!webcontainerPromise) {
    webcontainerPromise = WebContainer.boot();
  }
  const wc = await webcontainerPromise;

  // Wire the server-ready event once. WebContainer emits this when code inside
  // the container starts listening on a port (e.g. app.listen(3000)). The `url`
  // is a real, browser-reachable preview origin for that in-container server —
  // this is what the API tester / preview should target (no host server needed).
  if (!serverReadyWired) {
    serverReadyWired = true;
    wc.on("server-ready", (port: number, url: string) => {
      serverReadyCbs.forEach((cb) => cb(port, url));
    });
  }
  return wc;
}

/** Subscribe to WebContainer server-ready events. Returns an unsubscribe fn. */
export function onServerReady(cb: ServerReadyCb): () => void {
  serverReadyCbs.add(cb);
  return () => {
    serverReadyCbs.delete(cb);
  };
}

export function resetWebContainer() {
  webcontainerPromise = null;
  serverReadyWired = false;
  serverReadyCbs.clear();
}

/**
 * Convert a flat `{ "/src/di/x.ts": "contents" }` map into the nested
 * `FileSystemTree` that `WebContainer.mount` actually requires.
 *
 * The previous code mounted flat keys directly, so a key like
 * "src/di/serviceContainer.ts" was treated as a single literal filename rather
 * than nested directories. That left the directory structure non-existent, so
 * `tsx src/app.ts` and relative imports (`./di/serviceContainer.js`) failed.
 * Building a proper tree fixes multi-file project execution.
 */
export function buildFileSystemTree(files: { [path: string]: string }): FileSystemTree {
  const root: FileSystemTree = {};

  for (const [rawPath, contents] of Object.entries(files)) {
    const parts = rawPath.replace(/^\/+/, "").split("/").filter(Boolean);
    if (parts.length === 0) continue;

    let cursor: FileSystemTree = root;
    parts.forEach((part, index) => {
      const isLeaf = index === parts.length - 1;
      if (isLeaf) {
        cursor[part] = { file: { contents } };
      } else {
        let node = cursor[part] as { directory: FileSystemTree } | undefined;
        if (!node || !("directory" in node)) {
          node = { directory: {} };
          cursor[part] = node;
        }
        cursor = node.directory;
      }
    });
  }

  return root;
}
